import * as joint from '@joint/core';
import { Node } from './node-base';
import { DrawnRectangle, TextBox } from './drawable-elements';
import { MiscellaneousLink } from './flow-links';
import { DefaultShapeColors, linkTypeColors, applyConnectorStyleMixin } from './constants';

export function applyGraphBackCompatAndCleanup(graph: joint.dia.Graph): void {
    // Elements
    graph.getElements().forEach(element => {
        if (element instanceof Node) {
            let markup = element.get('markup') as any[] | any;
            if (!Array.isArray(markup)) {
                markup = [markup];
            }

            // Remove legacy function icon <image> or selectors that contained functionIcon
            const newMarkup = markup.filter((m: any) => {
                if (typeof m !== 'object' || !m) return true;
                if (m.selector === 'flag') return true;
                if (typeof m.selector === 'string' && m.selector.includes('functionIcon')) return false;
                if (m.tagName === 'image' && m.selector !== 'flag') return false;
                return true;
            });
            const hasFlag = newMarkup.some((m: any) => typeof m === 'object' && m && m.selector === 'flag');
            if (!hasFlag) {
                newMarkup.push({
                    tagName: 'image',
                    selector: 'flag',
                });
            }
            element.set('markup', newMarkup);

            // Replace spaces in legacy entityIds
            const entityIds = (element as Node).entityIds as string[];
            if (Array.isArray(entityIds) && entityIds.some(id => typeof id === 'string' && id.includes(' '))) {
                (element as Node).entityIds = entityIds.map(id => id.replace(/\s+/g, '_'));
            }

            // Ensure svgPath set for older saved graphs
            const type = element.get('type');
            if (type != null && !element.get('svgPath')) {
                element.set('svgPath', `/icons/${type}.svg`);
            }
            
            // Convert legacy BranchThirdParty to Branch or ThirdParty based on color
            if (type === 'BranchThirdParty') {
                const fillColor = element.attr('body/fill');
                // Assume Gray color indicates ThirdParty (external entity)
                if (fillColor === 'gray' || fillColor === '#52575B' || fillColor === DefaultShapeColors.ThirdPartyColor) {
                    element.set('type', 'ThirdParty');
                    element.set('svgPath', '/icons/ThirdParty.svg');
                } else {
                    // Assume any other color indicates Branch entity
                    element.set('type', 'Branch');
                    element.set('svgPath', '/icons/Branch.svg');
                }
            }
            
            // Ensure transparent background rect exists in markup of Branch, ThirdParty, and Partnership shapes
            if ((type === 'Branch' || type === 'ThirdParty' || type === 'BranchThirdParty' || type === 'Partnership')) {
                const hasBgRect = newMarkup.some((m: any) =>
                    typeof m === 'object'
                    && m.tagName === 'rect'
                    && m.selector === 'backgroundRect'
                );
                if (!hasBgRect) {
                    newMarkup.unshift({
                        tagName: 'rect',
                        selector: 'backgroundRect',
                    });
                    element.set('markup', newMarkup);
                    element.attr('backgroundRect/width', 'calc(w)');
                    element.attr('backgroundRect/height', 'calc(h)');
                    element.attr('backgroundRect/fill', 'transparent');
                }
            }
            // Ensure transparent body rect exists in markup of Group shapes
            else if (type === 'EntityGroup') {
                const hasTransparentBody =
                    newMarkup.some((m: any) => m.selector === 'body') && element.attr('body/fill') === 'transparent';
                if (hasTransparentBody) {
                    return;
                }
                // rename existing body to body1
                const bodyIndex = newMarkup.findIndex((m: any) => m.selector === 'body');
                if (bodyIndex !== -1) {
                    newMarkup[bodyIndex].selector = 'body1';
                }
                // add new body with transparent fill
                newMarkup.unshift({
                    tagName: 'rect',
                    selector: 'body',
                });
                element.set('markup', newMarkup);
                // copy old body fill to body1, then set the new body fill to transparent
                element.attr('body1/fill', element.attr('body/fill'));
                element.attr('body/fill', 'transparent');
            }
        }
        // Legacy DrawnRectangle with inline label: migrate to a sibling TextBox
        else if (element instanceof DrawnRectangle) {
            const legacyText = element.attr('label/text');
            let markup = element.get('markup') as any[] | any;
            if (!Array.isArray(markup)) {
                markup = [markup];
            }
            const hasLabelSelector = markup.some((m: any) => typeof m === 'object' && m && m.selector === 'label');
            const hasLegacyLabelText = typeof legacyText === 'string' && legacyText.trim().length > 0;

            if (hasLabelSelector && hasLegacyLabelText) {
                const cleanedMarkup = markup.filter((m: any) => !(typeof m === 'object' && m && m.selector === 'label'));
                element.set('markup', cleanedMarkup);
                // Clear label attrs if present
                element.attr('label/text', undefined);

                // Create TextBox above the rectangle with same text
                const text = hasLegacyLabelText ? legacyText : '';
                const rectPos = element.position();
                const rectSize = element.size();

                const textHeight = 40;
                const margin = 8;

                const textBox = new TextBox({
                    size: { width: rectSize.width, height: textHeight },
                    position: { x: rectPos.x, y: rectPos.y - textHeight - margin },
                });
                if (typeof text === 'string') {
                    textBox.setLabel(text);
                }
                graph.addCell(textBox);
            }
        }
    });

    // Links
    graph.getLinks().forEach(link => {
        // Remove legacy Miscellaneous links
        if (link instanceof MiscellaneousLink) {
            link.remove();
            return;
        }
        // Remove standard links that used the Misc color
        if (link.attributes.type === 'standard.Link' && link.attributes.attrs?.line?.stroke === '#EB8100') {
            link.remove();
            return;
        }
        // Upgrade standard links by color to the correct custom type
        if (link.attributes.type === 'standard.Link' && link.attributes.attrs?.line?.stroke) {
            const linkColor = link.attributes.attrs.line.stroke as string;
            const match = linkTypeColors.find(lc => lc.color === linkColor);
            if (match) {
                link.attributes.color = linkColor as any;
                link.attributes.type = match.type;
            }
        }
        // Ensure source and target selectors for older saved graphs
        link.attributes.source.selector = 'body';
        link.attributes.target.selector = 'body';
        
        // Apply connector style mixin for loaded links
        applyConnectorStyleMixin(link);
    });
}
